import React, { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import { X, Loader2, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  const size = Math.min(cropPixels.width, cropPixels.height);
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, size, size);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
}

export default function AvatarCropper({ imageSrc, onClose, onUploaded, endpoint = '/users/me/avatar' }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setCropPixels(pixels), []);

  const save = async () => {
    if (!cropPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, cropPixels);
      const form = new FormData();
      form.append('avatar', blob, 'avatar.jpg');
      const { data } = await api.put(endpoint, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto profilo aggiornata');
      onUploaded?.(data.avatar_url);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore upload');
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      data-testid="avatar-cropper"
    >
      <motion.div
        initial={{ y: 40, scale: 0.96 }} animate={{ y: 0, scale: 1 }}
        className="card-surface p-4 w-full max-w-sm space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">Modifica foto</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
        </div>

        <div className="relative w-full h-72 bg-black rounded-2xl overflow-hidden">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            style={{ containerStyle: { background: '#000' } }}
          />
        </div>

        <div className="flex items-center gap-3">
          <button data-testid="crop-zoom-reset" onClick={() => setZoom(1)} className="p-2 text-white/60 hover:text-[var(--gold)]">
            <RotateCcw className="w-4 h-4" />
          </button>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-[var(--gold)]"
            data-testid="crop-zoom-slider"
          />
        </div>

        <button
          data-testid="crop-save-btn"
          onClick={save}
          disabled={saving}
          className="btn-gold w-full py-3 flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Salva foto</>}
        </button>

        <p className="text-[10px] text-white/40 text-center">L'immagine sarà ritagliata e ottimizzata automaticamente.</p>
      </motion.div>
    </motion.div>
  );
}
